/**
 * Radiation monitoring layer renderer.
 * ScatterplotLayer with severity-coded colors based on z-score thresholds.
 * Confirmed readings (corroborated by multiple sources) get a solid outer ring.
 */

import { ScatterplotLayer } from '@deck.gl/layers';
import type { Layer } from '@deck.gl/core';
import type { NormalizedEvent } from '@/sources/base-source';
import type { RadiationReading } from '@/sources/built-in/radiation';

/** Layer IDs managed by this renderer. */
export const RADIATION_LAYER_IDS = new Set(['radiation-watch']);

/** Z-score thresholds for severity classification. */
const SEVERITY_THRESHOLDS = {
  critical: 4.0,   // z >= 4.0 — extreme anomaly
  high: 3.0,       // z >= 3.0 — significant anomaly
  elevated: 2.0,   // z >= 2.0 — elevated above normal
  normal: 0,       // z < 2.0 — within expected range
} as const;

/**
 * Extract radiation readings from normalized events — the source carries the
 * full reading in event.raw. Events without the numeric core are dropped.
 */
export function eventsToRadiationReadings(events: NormalizedEvent[]): RadiationReading[] {
  const readings: RadiationReading[] = [];
  for (const e of events) {
    const raw = e.raw as Partial<RadiationReading> | undefined;
    if (
      raw &&
      typeof raw.lat === 'number' &&
      typeof raw.lng === 'number' &&
      typeof raw.zScore === 'number'
    ) {
      readings.push(raw as RadiationReading);
    }
  }
  return readings;
}

/** Classify a z-score into a severity level. */
function classifyZScore(zScore: number): string {
  const absZ = Math.abs(zScore);
  if (absZ >= SEVERITY_THRESHOLDS.critical) return 'critical';
  if (absZ >= SEVERITY_THRESHOLDS.high) return 'high';
  if (absZ >= SEVERITY_THRESHOLDS.elevated) return 'elevated';
  return 'normal';
}

/** Get the fill color for a radiation reading — matching WorldMonitor exactly. */
export function getReadingColor(reading: RadiationReading): [number, number, number, number] {
  const severity = classifyZScore(reading.zScore);
  // WM uses a flat 3-way split: spike -> red, low-confidence -> dim orange, else -> orange
  if (severity === 'critical') return [255, 48, 48, 220];
  if (reading.confidence === 'low') return [255, 174, 0, 150];
  return [255, 174, 0, 200];
}

/** Get radius in meters — matching WorldMonitor exactly. */
export function getReadingRadius(reading: RadiationReading): number {
  const severity = classifyZScore(reading.zScore);
  const base = severity === 'critical' ? 26000 : 18000;
  if (reading.confirmed) return base * 1.15;
  // Low-confidence readings are smaller — matching WM
  if (reading.confidence === 'low') return base * 0.85;
  return base;
}

/** Create deck.gl layers for radiation monitoring data. */
export function createRadiationLayers(readings: RadiationReading[]): Layer[] {
  if (readings.length === 0) return [];

  // WM sets no layer opacity (DeckGLMap.ts:3528-3552) — the 220/200/150
  // fill alphas carry the encoding. White ring is the distinguishing sign.
  return [new ScatterplotLayer<RadiationReading>({
    id: 'radiation-watch-layer',
    data: readings,
    pickable: true,
    filled: true,
    stroked: true,
    radiusMinPixels: 6,
    radiusMaxPixels: 20,
    getPosition: (d) => [d.lng, d.lat],
    getFillColor: (d) => getReadingColor(d),
    getLineColor: [255, 255, 255, 200],
    lineWidthMinPixels: 2,
    getRadius: (d) => getReadingRadius(d),
    updateTriggers: {
      getFillColor: [readings.length],
      getRadius: [readings.length],
    },
  })];
}
